/**
 * SEO-friendly URL helpers.
 *
 * Pattern:  /:langSlug/products[/category[/subcategory]]
 *           /:langSlug/product/:productId
 *
 * langSlug is a human-readable slug like "english", "arabic", "french" — not
 * the i18n code ("en", "ar", "fr"). This makes the URL self-descriptive and
 * Google-friendly, e.g. /english/products/digital-products/ai-ebooks.
 *
 * The old query-string URLs (`/products?category=...&subcategory=...`) remain
 * valid for backward-compatibility (existing bookmarks, share links, emails).
 */

// i18n code <-> slug map (mirrors LanguageSelector languages list)
export const LANG_CODE_TO_SLUG = {
  en: 'english', ar: 'arabic', zh: 'chinese', nl: 'dutch', fr: 'french',
  de: 'german', hi: 'hindi', id: 'indonesian', it: 'italian', ja: 'japanese',
  ko: 'korean', ms: 'malay', pl: 'polish', pt: 'portuguese', ru: 'russian',
  es: 'spanish', sv: 'swedish', th: 'thai', tr: 'turkish', vi: 'vietnamese',
};

export const LANG_SLUG_TO_CODE = Object.fromEntries(
  Object.entries(LANG_CODE_TO_SLUG).map(([code, slug]) => [slug, code])
);

export const LANG_SLUGS = Object.values(LANG_CODE_TO_SLUG);

/**
 * Build an SEO-friendly products URL.
 * @param {string} langCode - i18n code (e.g. 'en')
 * @param {string|null} category
 * @param {string|null} subcategory
 */
export const buildProductsPath = (langCode = 'en', category = null, subcategory = null) => {
  const slug = LANG_CODE_TO_SLUG[langCode] || 'english';
  let path = `/${slug}/products`;
  if (category) path += `/${category}`;
  if (category && subcategory) path += `/${subcategory}`;
  return path;
};

/**
 * Build an SEO-friendly product detail URL.
 */
export const buildProductPath = (langCode = 'en', productId) => {
  const slug = LANG_CODE_TO_SLUG[langCode] || 'english';
  return `/${slug}/product/${productId}`;
};

/**
 * Parse a langSlug URL param; returns the i18n code or null if invalid.
 */
export const parseLangSlug = (slug) => LANG_SLUG_TO_CODE[slug?.toLowerCase()] || null;
